import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce, filter, isEqual } from 'lodash';
import { Icon } from 'semantic-ui-react';
import PropTypes from 'prop-types';

import { FormContext } from '../../../customise/contexts/form-context';
import { setUserInputOnCtrl } from '../../../form/reducer/form';
import { ControlPropType } from '../../ctrl_proptype';
import {
  Input,
  DDBody,
  DDHeader,
  DDWrapper,
  OptionsList,
  OptionsFilter,
  OptionButtons,
  OtherTextError,
  OptionsActions,
  SelectedOptions,
  NoOptPlaceholder,
  DDOptionsWrapper,
  OptionsOtherInputWrapper,
} from './field_dropdown.styled';

const regenerateValues = (inForm, ctrlValue, options, other, isMultiple) => {
  if (!inForm) return [];

  const lastValue = isMultiple && ctrlValue?.[ctrlValue?.length - 1];
  const isOtherValue = isMultiple && options.indexOf(lastValue) === -1 ? true : false;
  const reproducedValues = [];

  if (isMultiple) {
    for (let i = 0; i < ctrlValue?.length; i += 1) {
      if (options.indexOf(ctrlValue?.[i]) !== -1) {
        reproducedValues.push({
          key: options.indexOf(ctrlValue?.[i]),
          text: ctrlValue?.[i],
          value: ctrlValue?.[i],
        });
      }
    }

    if (other?.enabled && lastValue && isOtherValue) {
      reproducedValues.push({
        key: options.length,
        text: other?.label,
        value: lastValue,
        isOther: true,
      });
    }
  } else {
    reproducedValues.push({
      key: isOtherValue ? options.length : options.indexOf(ctrlValue),
      text: isOtherValue ? other?.label : ctrlValue,
      value: ctrlValue || other?.label,
      ...(other?.enabled && isOtherValue && { isOther: true }),
    });
  }

  return reproducedValues;
};

const getCtrlValue = (value, col, tr, inTable) => {
  if (inTable) return value?.[tr - 1]?.[col - 1] || null;
  return value;
};

const FieldDropdown = ({ options, other, isMultiple, ctrl, col, tr, inTable }) => {
  const dispatch = useDispatch();
  const ddListRef = useRef(null);
  const ddHeaderRef = useRef(null);
  const otherInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchResultRef = useRef(null);

  const { userCustomTexts } = useSelector((state) => state.form);
  const { inBuilder, inForm, currentPageNo } = useContext(FormContext);
  const { inPreviewPage: inBuilderPreview, previewViewPort } = useSelector((state) => state.builder.present);

  const obtainedValue = getCtrlValue(ctrl.value, col, tr, inTable);
  const reproducedValues = obtainedValue ? regenerateValues(inForm, obtainedValue, options, other, isMultiple) : null;

  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const [toggleDD, setToggleDD] = useState(false);
  const [otherInput, setOtherInput] = useState(false);
  const [otherTextError, setOtherTextError] = useState(false);
  const [values, setValues] = useState(obtainedValue ? reproducedValues : []);
  const [otherText, setOtherText] = useState(
    ctrl.value && reproducedValues?.length ? reproducedValues.find((o) => o.isOther)?.value : other?.label
  );

  // Create options structure
  const ddOptionsMain =
    inForm || inBuilderPreview
      ? [...options].map((option, index) => ({
          key: index,
          text: option,
          value: option,
        }))
      : [];

  // If other text is activated, create and add other text's special object
  if ((inForm || inBuilderPreview) && other && other?.enabled) {
    ddOptionsMain.push({
      key: ddOptionsMain.length,
      text: other?.label,
      value: otherText || other?.label,
      isOther: true,
    });
  }

  const optionValues = ddOptionsMain.map((opt) => opt.value);
  const [ddOptions, setDdOptions] = useState([...ddOptionsMain]);

  // Utility function to store values in global state
  const storeValues = useCallback(
    (arr) => {
      const arrValues = isMultiple ? arr?.map((v) => v.value) : arr[0]?.value;
      const saveUpdatedCtrl = (newValue) => {
        dispatch(
          setUserInputOnCtrl({
            newValue,
            property: 'value',
            page: currentPageNo - 1,
            ctrlId: ctrl.internal_id,
            isValid: true,
            ...(inTable && { tr: tr - 1, col: col - 1 }),
          })
        );
      };

      if (isEqual(obtainedValue, arrValues)) return;

      if (arr.length <= 0 && obtainedValue !== null) saveUpdatedCtrl(null);
      else if (arrValues === null) return;

      if (arrValues !== null && arr.length) saveUpdatedCtrl(arrValues);
    },
    [ctrl.internal_id, currentPageNo, dispatch, isMultiple, obtainedValue, col, tr, inTable]
  );

  // Constructive usage: Detect/identify if option type is of other text or not
  const detectIfOther = (opt) => {
    if (opt.isOther) {
      return otherText || other?.label;
    }
    return opt.text;
  };

  // Toggle DDBody (Dropdown body)
  const handleToggleDD = () => {
    if (inForm || inBuilderPreview) {
      setToggleDD(!toggleDD);
      if (toggleDD) ddListRef.current.focus();
      storeValues(values);
    }
  };

  // Eliminate non alphanumeric characters from any string
  const onlyAlphanumerics = (arg) => {
    return arg.replace(/[^a-zA-Z]/g, '');
  };

  const handleToggleOtherInput = (opt, state) => {
    if (opt.isOther) {
      setOtherInput(state);
      if (state) {
        otherInputRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  };

  //* ** ACTION EVENT HANDLERS ** *//
  const handleOptionClick = (opt) => {
    if (!inForm) {
      setToggleDD(false);
      return;
    }

    const newValues = [...values];

    // If in single mode
    if (!isMultiple) {
      if (!values.some((v) => v.key === opt.key)) {
        newValues.splice(0, 1, opt);
        setValues(newValues);
        handleToggleOtherInput(opt, true);
      } else {
        newValues.splice(0, 1);
        setValues(newValues);
        handleToggleOtherInput(opt, false);
      }

      // Dont close Dropdown if other text
      // Allows access to the other text input
      if (!opt.isOther) setToggleDD(false);
      storeValues(newValues);
    }

    // if in multi mode
    else if (!values.some((v) => v.key === opt.key)) {
      setValues((prev) => [...prev, opt]);
      handleToggleOtherInput(opt, true);
    } else {
      setValues((prev) => prev.filter((v) => v.key !== opt.key));
      handleToggleOtherInput(opt, false);
    }
  };

  const handleSearchFilter = debounce((e) => {
    const result = filter(ddOptionsMain, (opt) => opt.text.toLowerCase().includes(e.target.value.toLowerCase()));
    searchResultRef.current = result;
    setDdOptions(result);
  }, 250);

  const handleOtherTextChange = (e) => {
    if (optionValues.find((val) => val.toLowerCase() === e.target.value.toLowerCase())) {
      setOtherTextError(true);
      setOtherText(other?.label);
    } else {
      setOtherTextError(false);
      setOtherText(e.target.value);
    }

    // Alter just the value of only the `other text`object
    setValues((prev) =>
      prev.map((v) =>
        v.key === ddOptionsMain[ddOptionsMain.length - 1].key ? { ...v, value: e.target.value || other?.label } : v
      )
    );
  };

  const handleSelectedClick = (e, val) => {
    if (isMultiple) e.stopPropagation();

    if (val && toggleDD) {
      handleToggleOtherInput(val, true);
      // document.querySelector(`#DDOptionsList #${onlyAlphanumerics(detectIfOther(val))}`).scrollIntoView({
      //   behavior: 'smooth',
      //   block: 'center',
      // });
    }
  };

  const handleSelectAll = () => {
    if (!inForm) return;

    let allValues;
    if (query) {
      const queryResult = searchResultRef.current;
      const filteredValues = queryResult.filter(({ key: id1 }) => !values.some(({ key: id2 }) => id2 === id1));
      allValues = values.length ? values.concat(filteredValues) : queryResult;
      setValues([...allValues]);
    } else {
      allValues = ddOptionsMain;
      setValues([...allValues]);
    }

    if (allValues.length && allValues.find((v) => v.isOther)) {
      setOtherInput(true);
    }
  };

  const handleClear = () => {
    if (!inForm) return;

    setValues([]);
    setOtherInput(false);
  };

  const handleDone = () => {
    if (!inForm) return;

    setToggleDD(false);
    storeValues(values);
  };

  const handleUnselect = (e, val) => {
    e.stopPropagation();
    const newValues = [...values].filter((v) => v.key !== val.key);
    if (!toggleDD) {
      storeValues(newValues);
    }
    setValues(newValues);

    if (val.isOther) {
      setOtherInput(false);
    }
  };

  //* ** ACCESSIBILITY CONCERNS ** *//
  const handleKeys = (e) => {
    if (e.key === 'Enter' && !toggleDD) {
      e.preventDefault();
      setToggleDD(true);
    }
    if (e.key === 'Escape' && toggleDD) {
      e.preventDefault();
      setToggleDD(false);
    }

    // Prevent delete of selected items on backspace if input fields are on focus
    if (searchInputRef.current === document.activeElement || otherInputRef.current === document.activeElement) return;

    // Delete selected items on backspace
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      if (values?.length) {
        const newvalues = [...values];
        newvalues.splice(values.length - 1, 1);
        setValues(newvalues);
      } else {
        setToggleDD(false);
      }
    }
  };

  // List Items are accessible by keyboard up/down arrow keys
  // and enter key selects list item that has cursor.
  // TODO: The cursor focus is still laggy. Idea for effectiveness required.
  const handleItemPressed = (e, opt) => {
    if (e.key === 'ArrowDown') {
      setCursor((prevState) => (prevState === ddOptions.length - 1 ? 0 : prevState + 1));
    } else if (e.key === 'ArrowUp') {
      setCursor((prevState) => (prevState === 0 ? ddOptions.length - 1 : prevState - 1));
    }

    // Select focused option on enter key
    if (e.key === 'Enter') {
      handleOptionClick(opt);
    }
  };

  // Detect click outside for DD toggle
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ddListRef?.current?.contains(e.target) || ddHeaderRef?.current?.contains(e.target)) {
        return;
      }
      storeValues(values);
      setToggleDD(false);
    };
    if (toggleDD) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [toggleDD, storeValues, values]);

  return (
    <DDWrapper id={`fpl-${ctrl.internal_id}`} tabIndex="0" onKeyUp={handleKeys} inTable={inTable}>
      <DDHeader
        role="button"
        ref={ddHeaderRef}
        aria-haspopup="true"
        onClick={handleToggleDD}
        notInBuilder={!inBuilder}
        aria-controls="dropdown-list"
        className={toggleDD ? 'toggleDD' : ''}>
        <DDOptionsWrapper>
          <div>
            {values.length ? (
              <SelectedOptions>
                {values
                  .sort((a, b) => a.key - b.key)
                  .map((val) => (
                    <span
                      key={val.key}
                      className={`selected ${isMultiple ? 'is-multiple' : ''}`}
                      onClick={(e) => handleSelectedClick(e, val)}>
                      {detectIfOther(val)}

                      {isMultiple && (
                        <button className="unselect" onClick={(e) => handleUnselect(e, val)}>
                          &#10006;
                        </button>
                      )}
                    </span>
                  ))}
              </SelectedOptions>
            ) : (
              <NoOptPlaceholder notInBuilder={!inBuilder}>
                {isMultiple
                  ? userCustomTexts.select_option || 'Select one or more options'
                  : userCustomTexts.select_option || 'Select an option'}
              </NoOptPlaceholder>
            )}
          </div>

          <Icon name="caret down" className="caret-down" />
        </DDOptionsWrapper>
      </DDHeader>
      {toggleDD && (
        <DDBody ref={ddListRef} id="dropdown-list">
          <OptionsActions inTable={inTable} isMultiple={isMultiple} inMobilePreview={previewViewPort === 'mobile'}>
            <OptionsFilter>
              <Input
                type="text"
                value={query}
                ref={searchInputRef}
                placeholder={userCustomTexts.filter_label || 'Search for option(s)'}
                onChange={(e) => {
                  setQuery(e.target.value);
                  handleSearchFilter(e);
                }}
              />
            </OptionsFilter>
            {inForm && (
              <OptionButtons>
                {isMultiple && (
                  <>
                    <button type="button" className="clear" onClick={() => values.length > 0 && handleClear()}>
                      {userCustomTexts.select_none || 'Clear All'}
                    </button>
                    <button type="button" className="all" onClick={handleSelectAll}>
                      {userCustomTexts.select_all || 'Select All'}
                    </button>
                  </>
                )}

                <button type="button" className="done" onClick={handleDone}>
                  {userCustomTexts.done || 'Done'}
                </button>
              </OptionButtons>
            )}
          </OptionsActions>
          {ddOptions.length ? (
            <OptionsList id="DDOptionsList" role="list">
              {ddOptions.map((opt, index) => (
                <li
                  tabIndex="0"
                  key={opt.key}
                  onClick={() => handleOptionClick(opt)}
                  id={onlyAlphanumerics(detectIfOther(opt))}
                  onKeyUp={(e) => handleItemPressed(e, ddOptions[cursor])}
                  className={values.find((v) => v.key === opt.key) || cursor === index ? 'inSelected' : ''}>
                  {opt.text}
                </li>
              ))}

              {other && other?.enabled && otherInput && (
                <OptionsOtherInputWrapper>
                  {otherTextError && (
                    <OtherTextError>The value you entered already exists. Search the list to select it</OtherTextError>
                  )}
                  <Input
                    type="text"
                    value={otherText}
                    ref={otherInputRef}
                    onChange={handleOtherTextChange}
                    placeholder="Edit other text option"
                  />
                </OptionsOtherInputWrapper>
              )}
            </OptionsList>
          ) : (
            <NoOptPlaceholder notInBuilder={!inBuilder}> No option(s) found</NoOptPlaceholder>
          )}
        </DDBody>
      )}
    </DDWrapper>
  );
};

FieldDropdown.defaultProps = {
  tr: null,
  col: null,
  other: null,
  inTable: false,
  isMultiple: false,
};

FieldDropdown.propTypes = {
  inTable: PropTypes.bool,
  isMultiple: PropTypes.bool,
  other: PropTypes.shape({
    enabled: PropTypes.bool,
    label: PropTypes.string,
  }),
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  tr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  col: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
};

export default FieldDropdown;
